import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaLicensePlatesGuidePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">License Plates Overview</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada License Plates Guide</h1>
        <p className="text-obsidian-300">Complete overview of Nevada license plate types, fees, and important rules about plate ownership.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-amber-800 font-bold text-sm">Nevada Plate Rule: Plates Stay With the Owner</p>
          <p className="text-amber-700 text-xs mt-1">In Nevada, license plates belong to the <strong>registered owner</strong>, not the vehicle. When you sell a vehicle, you keep your plates. You must either transfer them to another vehicle or surrender them to the DMV within 30–60 days.</p>
        </div>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Types of Nevada License Plates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Standard Plates', desc: 'The default Nevada license plate. Issued in pairs for most passenger vehicles.', fee: '$7.00/pair' },
              { title: 'Specialty/Charitable Plates', desc: 'Plates supporting various causes, organizations, or Nevada themes. Additional fees support the associated cause.', fee: '$61 initial / $30 renewal' },
              { title: 'Personalized Plates', desc: 'Custom letter/number combinations of your choice. Subject to availability and DMV approval.', fee: '$96 initial / $50 renewal' },
              { title: 'Veteran Plates', desc: 'For eligible veterans. Various designs available based on branch of service and awards.', fee: 'Varies by type' },
              { title: 'Disabled Parking Plates', desc: 'For individuals with qualifying disabilities. Requires physician certification.', fee: 'Standard plate fee' },
              { title: 'Motorcycle Plates', desc: 'Single plate issued for motorcycles. Different size than standard vehicle plates.', fee: '$7.00' },
            ].map(item => (
              <div key={item.title} className="border border-obsidian-100 rounded-lg p-4">
                <div className="flex justify-between items-start mb-1">
                  <p className="font-semibold text-obsidian-900 text-sm">{item.title}</p>
                  <span className="text-xs bg-gold-50 text-gold-700 px-2 py-0.5 rounded font-medium whitespace-nowrap ml-2">{item.fee}</span>
                </div>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Plate Surrender Rules</h2>
          <p className="text-sm">When selling a vehicle, you must either:</p>
          <ul className="mt-3 space-y-2">
            {[
              'Transfer the plates to another vehicle you own within 30–60 days',
              'Surrender the plates to the Nevada DMV within 30–60 days',
              'Failure to surrender or transfer plates can result in continued registration fees',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3 text-sm">
                <i className="ri-arrow-right-s-line text-gold-500 flex-shrink-0 mt-0.5"></i>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need New Plates or a Transfer?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services handles all license plate transactions in Las Vegas.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaLicensePlatesGuidePage;
